import jwtDecode from 'jwt-decode';
import useAuth from '../../hooks/useAuth';
import { axiosAuthInstance, axiosIntegrationInstance } from '../axios';

export async function login(username, password) {
  const data = {
    username,
    password,
  };
  const response = await axiosAuthInstance.post('/user/login', JSON.stringify(data));
  return response;
}

export async function mfaVerify(username, mfaVerificationCode, signUpToken) {
  const response = await axiosAuthInstance.post('/user/login/verifyMfa', { username, mfaVerificationCode }, {
    headers: {
      Authorization: `Bearer ${signUpToken}`,
    },
  });
  return response;
}

export function useRefreshTokens() {
  const { setAuth } = useAuth();

  const refresh = async (forceDbLookup = false) => {
    try {
      // Add query parameter to force database lookup if needed
      const url = forceDbLookup ? '/user/refresh?forceDbLookup=true' : '/user/refresh';
      const response = await axiosAuthInstance.get(url);
      const { accessToken } = response.data;
      const decoded = jwtDecode(accessToken);

      setAuth((prev) => ({
        ...prev,
        accessToken,
      }));
      return { ...response.data, siteIds: decoded.siteIds };
    } catch {
      setAuth({});
      return null;
    }
  };
  return refresh;
}

export function usePointClickCareRefreshTokens() {
  const refresh = async () => {
    try {
      const response = await axiosIntegrationInstance.get('/pointClickCare/refresh');
      return response.data;
    } catch {
      return null;
    }
  };
  return refresh;
}

export function useLogout() {
  const { setAuth, setPersist } = useAuth();
  const logout = async () => {
    await axiosAuthInstance.get('/user/logout');
    setPersist(false);
    setAuth({});
    localStorage.clear();
  };
  return logout;
}

export function useRevokePointClickCareAccessToken() {
  const { pointClickCareAuth } = useAuth();
  const revoke = async () => {
    try {
      const response = await axiosIntegrationInstance.post('/pointClickCare/revoke', {
        token: pointClickCareAuth.accessToken,
      });
      return response.data;
    } catch {
      return null;
    }
  };
  return revoke;
}

export async function sendEmail(username) {
  const response = await axiosAuthInstance.post('/user/sendPasswordResetEmail', JSON.stringify({ username }));
  return response.data;
}

export async function sendEmailMfaCode(signUpToken) {
  const response = await axiosAuthInstance.post('/user/mfaEmail/sendCode', undefined, {
    headers: {
      Authorization: `Bearer ${signUpToken}`,
    },
  });
  return response;
}

export async function validatePasswordResetToken(token) {
  const response = await axiosAuthInstance.post('/user/validatePasswordResetToken', undefined, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
}

export async function resetPassword(token, newPassword) {
  const data = {
    newPassword,
  };
  const response = await axiosAuthInstance.post('/user/resetPassword', JSON.stringify(data), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function sendSignUpEmail(axiosAuth, data) {
  const response = await axiosAuth.post('/user/sendSignUpEmail', JSON.stringify(data), {});
  return response.data;
}

export async function sendCreateAPIUserRequest(axiosAuth, requestBody) {
  const response = await axiosAuth.post('/user/api/signup', JSON.stringify(requestBody), {
  });
  return response.data;
}

export async function validateSignUpToken(token) {
  const data = {
    token,
  };
  const response = await axiosAuthInstance.post('/user/validateSignUpToken', JSON.stringify(data), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
}

export async function sendSmsVerification(phoneNumber, token) {
  await axiosAuthInstance.post('/user/sendSmsVerification', JSON.stringify({ phoneNumber }), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function sendVerificationCode(username, phoneNumber, signUpToken) {
  const response = await axiosAuthInstance.post('/user/mfa/signup', JSON.stringify({
    username,
    phoneNumber,
  }), {
    headers: {
      Authorization: `Bearer ${signUpToken}`,
    },
  });
  return response;
}

export async function signUpUser(tokenData, userInput, signupToken) {
  const response = await axiosAuthInstance.post('/user/signup', {
    username: tokenData.username,
    first_name: userInput.firstName,
    last_name: userInput.lastName,
    password: userInput.password,
    permissions: tokenData.permissions,
    siteIds: tokenData.siteIds,
  }, {
    headers: {
      Authorization: `Bearer ${signupToken}`,
    },
  });
  return response;
}

export async function verifyMfaCode({
  mfaVerificationCode,
  signupToken,
  mfaMethod,
  username,
  phoneNumber,
}) {
  const response = await axiosAuthInstance.post('/user/mfa/signup/verify', {
    mfaVerificationCode,
    mfaMethod,
    username,
    phoneNumber,
  }, {
    headers: {
      Authorization: `Bearer ${signupToken}`,
    },
  });
  return response;
}
